import { normalizeInlineContainers, type IInlineHtmlRegion } from "./inlineHtml";
import { editorToolbar, editorPlugins, editorFontSizes } from "../../components/common/editorOptions";
import type { Editor } from "hugerte";
import { inlineToolbarCommands } from "./inlineToolbarProtocol";
import hugerteDarkSkinCss from "hugerte/skins/ui/oxide-dark/skin.min.css?raw";
import hugerteSkinCss from "hugerte/skins/ui/oxide/skin.min.css?raw";

// The preview remains an opaque-origin sandbox. Edits travel over a narrowly
// scoped postMessage channel; no preview code can access the parent document.
function installInlineEditing(token: string, parentOrigin: string, options: { commands: readonly string[]; toolbar: string; plugins: string; fontSizes: string; plainText?: boolean; scripts: { attributes: string[][]; text: string | null }[] }) {
  const root = document.querySelector<HTMLElement>('[data-inline-html-root]');
  if (!root) return;
  // Page/section preview wrappers are applied after building this document.
  // Keep editor chrome out of their flex/grid layout and theme selectors.
  const toolbar = document.getElementById('inline-html-hugerte-toolbar');
  if (toolbar) document.body.prepend(toolbar);
  let editor: Editor | undefined;
  let savedSelection: ReturnType<Editor["selection"]["getBookmark"]> | undefined;
  const rememberSelection = () => {
    if (!editor || interacting || editor.mode.get() === 'readonly') return;
    const node = editor.selection.getNode();
    if (node === root || root.contains(node)) savedSelection = editor.selection.getBookmark(2, true);
  };
  const reportEditingAnchor = () => {
    const field = root.getBoundingClientRect();
    window.parent.postMessage({ type: 'inline-html-editing-anchor', token,
      bounds: { left: field.left, top: field.top, width: field.width, height: field.height } }, parentOrigin);
  };
  const reportToolbarState = () => {
    if (!editor) return;
    rememberSelection();
    reportEditingAnchor();
    window.parent.postMessage({ type: 'inline-html-toolbar-state', token, state: {
      ready: true, bold: editor.queryCommandState('Bold'), italic: editor.queryCommandState('Italic'),
      underline: editor.queryCommandState('Underline'), strikethrough: editor.queryCommandState('Strikethrough'),
      unorderedList: editor.queryCommandState('InsertUnorderedList'), orderedList: editor.queryCommandState('InsertOrderedList'),
      canUndo: editor.undoManager.hasUndo(), canRedo: editor.undoManager.hasRedo(),
      format: String(editor.queryCommandValue('FormatBlock') || 'p').toLowerCase(),
      fontName: String(editor.queryCommandValue('FontName') || ''), fontSize: String(editor.queryCommandValue('FontSize') || '')
    } }, parentOrigin);
  };
  let interacting = false;
  const restoreActions = (scope: ParentNode, removeMetadata = false) => {
    const elements = Array.from(scope.querySelectorAll<HTMLElement>('[data-cms-preview-actions]'));
    if (scope instanceof HTMLElement && scope.hasAttribute('data-cms-preview-actions')) elements.unshift(scope);
    elements.forEach(element => {
      const actions = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(element.getAttribute('data-cms-preview-actions')!), char => char.charCodeAt(0))));
      for (const [name, value] of Object.entries(actions)) element.setAttribute(name, String(value));
      if (removeMetadata) element.removeAttribute('data-cms-preview-actions');
    });
  };
  document.addEventListener('pointerdown', event => {
    const target = event.target as Element;
    const block = target.closest('[data-inline-html-region]');
    if (interacting || !block || block.contains(root)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    emit('inline-html-change');
    window.parent.postMessage({ type: 'inline-html-select-region', token, index: Number(block.getAttribute('data-inline-html-region')) }, parentOrigin);
  }, true);
  let scriptsStarted = false;
  const startScripts = async () => {
    if (scriptsStarted) return;
    scriptsStarted = true;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    const scripts: Comment[] = [];
    while (walker.nextNode()) {
      const node = walker.currentNode as Comment;
      if (node.data.startsWith('sbcms-inline-script:')) scripts.push(node);
    }
    const contentScripts = scripts.map(placeholder => JSON.parse(decodeURIComponent(placeholder.data.slice('sbcms-inline-script:'.length))));
    const sameScript = (a: { attributes: string[][]; text: string | null }, b: { attributes: string[][]; text: string | null }) =>
      a.text === b.text && JSON.stringify(a.attributes) === JSON.stringify(b.attributes);
    // Renderer-provided scripts include site behaviour and its library dependencies.
    // These belong to the preview, never to the saved BodyText property.
    const allScripts = [...options.scripts, ...contentScripts.filter(data => !options.scripts.some(script => sameScript(data, script)))];
    for (const data of allScripts) {
      const script = document.createElement('script');
      for (const [name, value] of data.attributes) script.setAttribute(name, value);
      script.textContent = data.text;
      // Keep executable copies outside the saved content; source placeholders remain.
      const loaded = script.src || script.type === 'module'
        ? new Promise<void>(resolve => { script.onload = () => resolve(); script.onerror = () => resolve(); })
        : Promise.resolve();
      document.body.appendChild(script);
      await loaded;
    }
    // Components commonly initialise their buttons in a DOMContentLoaded listener.
    document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true }));
  };
  let pageList: unknown[] = [];
  let imageList: unknown[] = [];
  let overlayOpen = false;
  let fullscreenOpen = false;
  let visibleToolbarSpace = 500;
  const resetToolbarPosition = () => {
    const host = document.getElementById('inline-html-hugerte-toolbar');
    if (host) host.style.transform = 'none';
  };
  const positionDialogs = () => {
    const toolbar = document.getElementById('inline-html-hugerte-toolbar');
    if (!toolbar) return;
    // HugeRTE inserts its popup sink into fixed_toolbar_container. A scroll
    // transform on that container otherwise gets applied to the popup twice.
    toolbar.querySelectorAll<HTMLElement>('.tox-hugerte-aux').forEach(sink => document.body.appendChild(sink));
    const anchor = toolbar.getBoundingClientRect();
    const visibleTop = Math.max(0, anchor.top);
    const visibleHeight = Math.max(0, Math.min(window.innerHeight - visibleTop, visibleToolbarSpace));
    document.querySelectorAll<HTMLElement>('.tox-dialog').forEach(dialog => {
      // Centre within the part of this frame visible in the designer, rather
      // than the full component document (which can be several screens tall).
      dialog.style.maxHeight = `${Math.max(88, visibleHeight - 24)}px`;
      const bounds = dialog.getBoundingClientRect();
      const left = Math.max(12, (window.innerWidth - bounds.width) / 2);
      const top = Math.max(12, visibleTop + (visibleHeight - bounds.height) / 2);
      dialog.style.position = 'fixed';
      dialog.style.left = `${left}px`;
      dialog.style.top = `${top}px`;
      dialog.style.margin = '0';
    });
  };
  // HugeRTE focuses the whole inline root when restoring a selection, and
  // focuses dialog inputs before OpenWindow fires. In a tall iframe either
  // default focus scroll can move the designer away from the selected content.
  // Scope this override to this editor's content/chrome inside the sandbox.
  const nativeFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (focusOptions?: FocusOptions) {
    const editorTarget = this === root || root.contains(this) || Boolean(this.closest('.tox, #inline-html-hugerte-toolbar'));
    const uiFocus = this.closest('.tox, #inline-html-hugerte-toolbar') || document.activeElement?.closest('.tox, #inline-html-hugerte-toolbar');
    if (editorTarget && uiFocus && !fullscreenOpen) window.parent.postMessage({ type: 'inline-html-preserve-scroll', token }, parentOrigin);
    if (editorTarget && this.closest('.tox-dialog')) positionDialogs();
    nativeFocus.call(this, editorTarget ? { ...focusOptions, preventScroll: true } : focusOptions);
  };
  toolbar?.addEventListener('pointerdown', () => {
    if (!fullscreenOpen) window.parent.postMessage({ type: 'inline-html-preserve-scroll', token }, parentOrigin);
  }, true);
  window.addEventListener('resize', positionDialogs);
  const original = root.innerHTML;
  const emit = (type: string) => {
    const clone = root.cloneNode(true) as HTMLElement;
    if (editor && !interacting) clone.innerHTML = editor.getContent();
    clone.querySelectorAll('[data-inline-original-events]').forEach(node => {
      const attributes = JSON.parse(node.getAttribute('data-inline-original-events')!);
      for (const [name, value] of attributes) node.setAttribute(name, value);
      node.removeAttribute('data-inline-original-events');
    });
    const comments = document.createTreeWalker(clone, NodeFilter.SHOW_COMMENT);
    const placeholders: Comment[] = [];
    while (comments.nextNode()) placeholders.push(comments.currentNode as Comment);
    placeholders.filter(node => node.data.startsWith('sbcms-inline-script:')).forEach(node => {
      const script = document.createElement('script');
      const data = JSON.parse(decodeURIComponent(node.data.slice('sbcms-inline-script:'.length)));
      for (const [name, value] of data.attributes) script.setAttribute(name, value);
      script.textContent = data.text;
      node.replaceWith(script);
    });
    restoreActions(clone, true);
    window.parent.postMessage({ type, token, html: options.plainText ? clone.textContent ?? "" : clone.innerHTML }, parentOrigin);
  };
  root.setAttribute('contenteditable', options.plainText ? 'plaintext-only' : 'true');
  root.setAttribute('role', 'textbox');
  root.setAttribute('aria-label', 'Edit HTML content directly');
  root.setAttribute('aria-multiline', options.plainText ? 'false' : 'true');
  document.addEventListener('keydown', event => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); if (!interacting) emit('inline-html-save'); }
    if (event.key === 'Escape' && !overlayOpen) { event.preventDefault(); window.parent.postMessage({ type: 'inline-html-cancel-request', token }, parentOrigin); }
    if (!interacting && options.plainText && event.key === 'Enter' && root.contains(event.target as Node)) event.preventDefault();
  });
  const deactivate = () => {
    if (overlayOpen) return;
    if (editor) {
      editor.selection.setCursorLocation(root, 0);
      editor.selection.controlSelection.hideResizeRect();
      editor.mode.set('readonly');
      editor.fire('blur');
    }
    root.querySelectorAll('[data-mce-selected]').forEach(node => node.removeAttribute('data-mce-selected'));
    window.getSelection()?.removeAllRanges();
    root.blur();
    // HugeRTE can restore an object selection at the end of its blur handler.
    requestAnimationFrame(() => {
      if (editor?.mode.get() !== 'readonly') return;
      editor.selection.controlSelection.hideResizeRect();
      root.querySelectorAll('[data-mce-selected]').forEach(node => node.removeAttribute('data-mce-selected'));
      window.getSelection()?.removeAllRanges();
    });
  };
  // HugeRTE's selection restoration may run after blur/animation callbacks.
  // Readonly preview content must never retain its object-selection markers.
  new MutationObserver(records => {
    if (editor?.mode.get() !== 'readonly') return;
    records.forEach(record => {
      const node = record.target as Element;
      if (node.hasAttribute('data-mce-selected')) node.removeAttribute('data-mce-selected');
    });
  }).observe(root, { subtree: true, attributes: true, attributeFilter: ['data-mce-selected'] });
  root.addEventListener('pointerdown', () => {
    if (!interacting && editor && editor.mode.get() === 'readonly') editor.mode.set('design');
  });
  root.addEventListener('focusin', () => { if (!interacting) { reportEditingAnchor(); emit('inline-html-focus'); } });
  window.addEventListener('blur', () => {
    // Let focus reach the parent Save/Cancel buttons before it checks the target.
    window.setTimeout(() => { if (!overlayOpen) emit('inline-html-blur'); }, 0);
  });
  document.addEventListener('pointerdown', event => {
    const target = event.target as Element;
    if (!overlayOpen && !root.contains(target) && !target.closest('.tox, #inline-html-hugerte-toolbar')) {
      deactivate(); emit('inline-html-blur');
    }
  });
  root.addEventListener('input', () => { if (!interacting) emit('inline-html-change'); });
  // Theme wrappers can provide padding, minimum heights and positioned
  // artwork beyond the editable fragment (for example a full-height hero).
  const sizingElements: HTMLElement[] = [];
  for (let node: HTMLElement | null = root; node && node !== document.body; node = node.parentElement) {
    sizingElements.push(node);
  }
  const toolbarHost = document.getElementById("inline-html-hugerte-toolbar");
  const reportHeight = () => {
    reportEditingAnchor();
    const contentHeight = Math.max(...sizingElements.map(node => node.getBoundingClientRect().height));
    const toolbarHeight = toolbarHost?.getBoundingClientRect().height ?? 0;
    // Collapsed bottom margins can put the renderer's trailing sizing marker
    // beyond the wrapper's own height. Include its document position as well;
    // viewport scrollHeight would prevent the frame shrinking after an edit.
    const contentBottom = Math.max(0, ...Array.from(document.body.children)
      .filter(node => node !== toolbarHost && !node.classList.contains('tox-hugerte-aux'))
      .map(node => node.getBoundingClientRect().bottom + window.scrollY + (parseFloat(getComputedStyle(node).marginBottom) || 0)));
    window.parent.postMessage({ type: 'inline-html-height', token, height: Math.ceil(Math.max(contentHeight + toolbarHeight, contentBottom)) + 2 }, parentOrigin);
  };
  const resize = new ResizeObserver(reportHeight);
  sizingElements.forEach(node => resize.observe(node));
  if (toolbarHost) resize.observe(toolbarHost);
  // Renderer sizing markers may be appended after HugeRTE initialises without
  // changing the wrapper's border-box size.
  new MutationObserver(reportHeight).observe(document.body, { childList: true });
  reportHeight();
  // Authoring clicks must never navigate the iframe or submit site forms.
  document.addEventListener('click', event => {
    const target = event.target as Element;
    if (!interacting && (root.contains(target) || target.closest('[data-inline-html-region]'))) {
      if (target.closest('a, button, input[type=submit]')) event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('submit', event => { if (!interacting) event.preventDefault(); }, true);
  root.addEventListener('paste', event => {
    if (interacting) return;
    event.preventDefault();
    document.execCommand('insertText', false, event.clipboardData?.getData('text/plain') ?? '');
  });
  // Keep scripts inert while preserving them in saved source.
  window.addEventListener('message', event => {
    if (event.source !== window.parent || event.origin !== parentOrigin || event.data?.token !== token) return;
    if (event.data.type === 'inline-html-toolbar-command') {
      if (!editor || interacting || !options.commands.includes(event.data.command)) return;
      const command = event.data.command as string;
      const value = event.data.value;
      if (command === 'FormatBlock' && !['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre'].includes(value)) return;
      if (command === 'FontName' && !['Arial', 'Georgia', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana', 'Courier New'].includes(value)) return;
      if (command === 'FontSize' && !['0.5rem', '0.75rem', '1rem', '1.25rem', '1.5rem', '1.75rem', '2rem', '2.5rem', '3rem', '4rem', '5rem'].includes(value)) return;
      if ((command === 'ForeColor' || command === 'HiliteColor') && (typeof value !== 'string' || !/^#[0-9a-f]{6}$/i.test(value))) return;
      const takesValue = ['FormatBlock', 'FontName', 'FontSize', 'ForeColor', 'HiliteColor'].includes(command);
      if (!takesValue && value !== undefined) return;
      const bookmark = savedSelection;
      editor.mode.set('design');
      editor.focus();
      if (bookmark) editor.selection.moveToBookmark(bookmark);
      if (command !== 'Focus') editor.execCommand(command, false, value);
      reportToolbarState();
      return;
    }
    if (event.data.type === 'inline-html-theme') {
      const skin = document.getElementById('sbcms-inline-hugerte-skin');
      if (skin && typeof event.data.skinCss === 'string') skin.textContent = event.data.skinCss;
      document.documentElement.style.setProperty('--inline-editor-surface', event.data.dark ? '#192438' : '#fff');
      return;
    }
    if (event.data.type === 'inline-html-snapshot-interaction') {
      emit('inline-html-interaction-snapshot');
      return;
    }
    if (event.data.type === 'inline-html-interact') {
      if (!interacting) emit('inline-html-focus');
      interacting = Boolean(event.data.enabled);
      document.documentElement.setAttribute('data-cms-editor-mode', interacting ? 'interact' : 'edit');
      if (interacting) {
        restoreActions(document);
        document.querySelectorAll('[data-inline-original-disabled]').forEach(element => element.setAttribute('disabled', ''));
      }
      document.body.toggleAttribute('data-inline-interacting', interacting);
      editor?.mode.set(interacting ? 'readonly' : 'design');
      root.setAttribute('contenteditable', interacting ? 'false' : 'true');
      root.querySelectorAll('[data-inline-original-events]').forEach(node => {
        for (const [name, value] of JSON.parse(node.getAttribute('data-inline-original-events')!)) {
          if (interacting) node.setAttribute(name, value);
          else node.removeAttribute(name);
        }
      });
      if (interacting) { deactivate(); void startScripts(); }
      else { emit('inline-html-change'); root.focus(); }
    }
    if (event.data.type === 'inline-html-toolbar-position') {
      const host = document.getElementById('inline-html-hugerte-toolbar');
      if (host) {
        const limit = Math.max(0, document.body.getBoundingClientRect().height - host.offsetHeight);
        visibleToolbarSpace = Number(event.data.availableHeight) || 500;
        const offset = fullscreenOpen ? 0 : Math.max(0, Math.min(Number(event.data.offset) || 0, limit));
        host.style.transform = `translateY(${offset}px)`;
        if (overlayOpen) positionDialogs();
      }
    }
    if (event.data.type === 'inline-html-overlay-ready' && editor) {
      if (fullscreenOpen) resetToolbarPosition();
      editor.fire('ResizeWindow');
      if (overlayOpen) requestAnimationFrame(positionDialogs);
    }
    if (event.data.type === 'inline-html-lists') { pageList = event.data.pages ?? []; imageList = event.data.images ?? []; }
    if (event.data.type === 'inline-html-activate') { editor?.mode.set('design'); root.focus(); }
    if (event.data.type === 'inline-html-undo') editor ? editor.undoManager.undo() : document.execCommand('undo');
    if (event.data.type === 'inline-html-redo') editor ? editor.undoManager.redo() : document.execCommand('redo');
    if (event.data.type === 'inline-html-deactivate') deactivate();
    if (event.data.type === 'inline-html-read') emit('inline-html-save');
    if (event.data.type === 'inline-html-lock') {
      if (editor) editor.mode.set(event.data.locked ? 'readonly' : 'design');
      else root.setAttribute('contenteditable', event.data.locked ? 'false' : 'true');
    }
    if (event.data.type === 'inline-html-cancel') {
      if (editor) editor.setContent(original);
      else root.innerHTML = original;
      root.blur();
    }
  });
  const tiny = (window as unknown as { hugerte?: { init: (options: object) => Promise<Editor[]> } }).hugerte;
  if (options.plainText) window.parent.postMessage({ type: 'inline-html-ready', token }, parentOrigin);
  if (tiny && !options.plainText) void tiny.init({
    target: root,
    inline: true,
    base_url: parentOrigin + '/vendor/hugerte',
    suffix: '.min',
    skin: false,
    content_css: false,
    menubar: false,
    branding: false,
    plugins: options.plugins,
    toolbar: false,
    font_size_formats: options.fontSizes,
    toolbar_mode: 'wrap',
    relative_urls: false,
    forced_root_block: 'div',
    link_list: (success: (items: unknown[]) => void) => success(pageList),
    image_list: (success: (items: unknown[]) => void) => success(imageList),
    file_picker_types: 'image',
    automatic_uploads: true,
    file_picker_callback: (callback: (url: string, meta: object) => void) => {
      const input = document.createElement('input');
      input.type = 'file'; input.accept = 'image/*';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file || !editor) return;
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result !== 'string' || !editor) return;
          const cache = editor.editorUpload.blobCache;
          const blob = cache.create(crypto.randomUUID(), file, reader.result.split(',')[1] ?? '');
          cache.add(blob); callback(blob.blobUri(), { title: file.name });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },
    fixed_toolbar_container: '#inline-html-hugerte-toolbar',
    valid_elements: '*[*]',
    verify_html: false,
    convert_urls: false,
    setup(instance: Editor) {
      editor = instance;
      instance.ui.registry.addButton('refreshmedia', {
        icon: 'reload', tooltip: 'Refresh site images',
        onAction: () => window.parent.postMessage({ type: 'inline-html-refresh-media', token }, parentOrigin)
      });
      instance.on('ScrollIntoView', event => {
        // Dialog/toolbar commands restore the existing selection; they should
        // not scroll it. Normal caret scrolling while typing remains enabled.
        if (overlayOpen || document.activeElement?.closest('.tox, #inline-html-hugerte-toolbar')) event.preventDefault();
      });
      instance.on('focus', () => { if (!interacting) emit('inline-html-focus'); });
      instance.on('input change Undo Redo', () => { if (!interacting) emit('inline-html-change'); });
      instance.on('NodeChange keyup mouseup Undo Redo AddUndo SetContent', reportToolbarState);
      let dialogs = 0;
      let fullscreen = false;
      let documentWidth: string | undefined;
      const reportOverlay = () => {
        // Native link/image/source dialogs need room outside a narrow column. Only their
        // surface expands; freeze the document width until the dialog closes.
        if (dialogs > 0 && !fullscreen && documentWidth === undefined) {
          documentWidth = document.body.style.width;
          document.body.style.width = getComputedStyle(document.body).width;
        } else if ((dialogs === 0 || fullscreen) && documentWidth !== undefined) {
          document.body.style.width = documentWidth;
          documentWidth = undefined;
        }
        document.body.toggleAttribute('data-inline-dialog-open', dialogs > 0 && !fullscreen);
        fullscreenOpen = fullscreen; overlayOpen = dialogs > 0 || fullscreen;
        if (fullscreen) resetToolbarPosition();
        window.parent.postMessage({ type: 'inline-html-overlay', token, open: overlayOpen, dialog: dialogs > 0 }, parentOrigin);
      };
      instance.ui.registry.addToggleButton('fullscreen', {
        icon: 'fullscreen', tooltip: 'Fullscreen',
        onAction: () => { fullscreen = !fullscreen; reportOverlay(); instance.fire('FullscreenStateChanged', { state: fullscreen }); },
        onSetup: api => {
          const update = () => api.setActive(fullscreen);
          instance.on('FullscreenStateChanged', update);
          return () => instance.off('FullscreenStateChanged', update);
        }
      });
      instance.on('OpenWindow', () => { dialogs++; positionDialogs(); reportOverlay(); requestAnimationFrame(positionDialogs); });
      instance.on('CloseWindow', () => { dialogs = Math.max(0, dialogs - 1); reportOverlay(); });
      instance.on('FullscreenStateChanged', event => { fullscreen = event.state; reportOverlay(); });
      instance.on('init', () => { reportToolbarState(); reportHeight(); window.parent.postMessage({ type: 'inline-html-ready', token }, parentOrigin); });
    }
  }).catch(() => { /* Plain-text editing remains available if the toolbar cannot load. */ });
}

export function inlineHtmlDocument(preview: string, html: string, token: string, parentOrigin: string, blockIndex?: number, fields?: { region: IInlineHtmlRegion; regions: IInlineHtmlRegion[] }): string {
  const doc = new DOMParser().parseFromString(preview, 'text/html');
  normalizeInlineContainers(doc, fields?.regions ?? []);
  const scripts = Array.from(doc.querySelectorAll('script')).map(script => ({
    attributes: Array.from(script.attributes).map(attr => [attr.name, attr.value]), text: script.textContent
  }));
  doc.querySelectorAll('script').forEach(script => script.remove());
  const raw = new DOMParser().parseFromString(html, 'text/html');
  // Scripts are retained as inert placeholders and restored only on save.
  raw.querySelectorAll('script').forEach(script => {
    const placeholder = raw.createComment('sbcms-inline-script:' + encodeURIComponent(JSON.stringify({
      attributes: Array.from(script.attributes).map(attr => [attr.name, attr.value]), text: script.textContent
    })));
    script.replaceWith(placeholder);
  });
  raw.querySelectorAll('*').forEach(node => {
    const events = Array.from(node.attributes).filter(attribute => /^on/i.test(attribute.name));
    if (!events.length) return;
    node.setAttribute('data-inline-original-events', JSON.stringify(events.map(attribute => [attribute.name, attribute.value])));
    events.forEach(attribute => node.removeAttribute(attribute.name));
  });
  const root = doc.createElement(fields?.region.plainText ? 'span' : 'div');
  root.setAttribute('data-inline-html-root', '');
  if (fields?.region.plainText) root.textContent = html;
  else root.innerHTML = raw.body.innerHTML;
  // Keep the renderer's outer component wrapper (classes/styles/id) without
  // saving any renderer-added srcsets, wrappers or script mutations as content.
  const children = Array.from(doc.body.children).filter(node => !['STYLE', 'LINK'].includes(node.tagName));
  const wrapper = children.length === 1 && children[0].tagName === 'DIV'
    ? children[0].cloneNode(false) as HTMLElement : null;
  if (fields?.region.selector) {
    fields.regions.forEach((region, index) => {
      if (region.selector) doc.querySelectorAll(region.selector)[region.index]?.setAttribute('data-inline-html-region', String(index));
    });
    const target = doc.querySelectorAll(fields.region.selector)[fields.region.index];
    if (!target) return preview;
    target.replaceChildren(root);
    if (fields?.region.actionLabel && target.hasAttribute('disabled')) {
      target.setAttribute('data-inline-original-disabled', '');
      target.removeAttribute('disabled');
    }
    // Authoring exposes the selected hidden item without running site scripts.
    const accordion = root.closest('.accordion-item');
    accordion?.querySelector('.accordion-collapse')?.classList.add('show');
    accordion?.querySelector('.accordion-button')?.classList.remove('collapsed');
    const tabLink = root.closest('.nav-link');
    const tabId = (tabLink?.getAttribute('data-bs-target') ?? tabLink?.getAttribute('href') ?? '').replace(/^#/, '');
    const pane = root.closest('.tab-pane') ?? (tabId ? doc.getElementById(tabId) : null);
    if (pane?.classList.contains('tab-pane')) {
      pane.parentElement?.querySelectorAll('.tab-pane').forEach(item => item.classList.remove('active', 'show'));
      pane.classList.add('active', 'show');
    }
    const slide = root.closest('.carousel-item');
    if (slide) {
      slide.parentElement?.querySelectorAll('.carousel-item').forEach(item => item.classList.remove('active'));
      slide.classList.add('active');
    }
  } else if (blockIndex !== undefined) {
    const targets = doc.querySelectorAll('.cms-htmlblock-content-container');
    targets.forEach((target, index) => target.setAttribute('data-inline-html-region', String(index)));
    const target = targets[blockIndex];
    if (!target) return preview;
    target.replaceChildren(root);
    if (fields?.region.actionLabel && target.hasAttribute('disabled')) {
      target.setAttribute('data-inline-original-disabled', '');
      target.removeAttribute('disabled');
    }
  } else {
    doc.body.replaceChildren(wrapper ?? root);
    if (wrapper) wrapper.appendChild(root);
  }
  doc.documentElement.classList.remove('hw100', 'hideOverFlow');
  doc.body.classList.remove('hw100');
  const dark = document.documentElement.getAttribute('data-theme') === 'midnight';
  const skin = doc.createElement('style');
  skin.id = 'sbcms-inline-hugerte-skin';
  skin.textContent = dark ? hugerteDarkSkinCss : hugerteSkinCss;
  doc.head.appendChild(skin);
  doc.documentElement.style.setProperty('--inline-editor-surface', dark ? '#192438' : '#fff');
  const style = doc.createElement('style');
  style.textContent = '\n[data-inline-html-root]{min-height:1.5em;outline:none;cursor:text;position:static!important}[data-inline-html-region]{cursor:text}[data-inline-html-region]:hover,[data-inline-html-root]:hover{outline:1px dashed #087c7880;outline-offset:-2px}[data-inline-html-root]:focus{outline:2px dashed #087c78;outline-offset:-2px}body[data-inline-interacting] [data-inline-html-region],body[data-inline-interacting] [data-inline-html-root]{cursor:auto;outline:none}#inline-html-hugerte-toolbar{position:sticky;top:0;z-index:1000;background:transparent}#inline-html-hugerte-toolbar .tox-editor-header--empty{display:none!important}body[data-inline-dialog-open]>*:not(.tox-hugerte-aux){visibility:hidden!important}body[data-inline-dialog-open] .tox-hugerte-aux{visibility:visible!important}body[data-inline-dialog-open] .tox-dialog{max-width:calc(100vw - 24px)!important}';
  doc.head.appendChild(style);
  const toolbar = doc.createElement('div');
  toolbar.id = 'inline-html-hugerte-toolbar';
  toolbar.setAttribute('contenteditable', 'false');
  doc.body.prepend(toolbar);
  const tiny = doc.createElement('script');
  tiny.src = parentOrigin + '/vendor/hugerte/hugerte.min.js';
  doc.body.appendChild(tiny);
  const bridge = doc.createElement('script');
  bridge.textContent = `(${installInlineEditing.toString()})(${JSON.stringify(token)},${JSON.stringify(parentOrigin)},${JSON.stringify({ commands: inlineToolbarCommands, toolbar: editorToolbar(), plugins: editorPlugins, fontSizes: editorFontSizes, scripts, plainText: fields?.region.plainText }).replace(/</g, '\\u003c')});`;
  doc.body.appendChild(bridge);
  return '<!DOCTYPE html>' + doc.documentElement.outerHTML;
}
